import { AppRoutes } from "@/constants/routes";
import { RequestTypes } from "@/constants/enums";
import { toaster } from "@/components/ui/toaster";
import { getFertSlotRequests } from "@/services/fert-slot-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import dayjs from "dayjs";
import { FertSlotRequestListResponse } from "./fert-slot-requests.schema";
import { NavigateFunction } from "react-router-dom";

interface FetchFertSlotRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<FertSlotRequestListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFertSlotRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchFertSlotRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getFertSlotRequests({
      ...filters,
      nroCuenta: filters?.nroCuenta?.value,
      tipoSolicitud: RequestTypes.CuposFertilizante,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de cupos de fertilizantes.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFiltersHandlerProps) => {
  const fechaEntrega = event?.fechaEntrega
    ? dayjs(event.fechaEntrega).format("YYYY-MM-DD")
    : undefined;

  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const kmDistancia =
    typeof event?.kmDistancia === "string" && event.kmDistancia.trim() !== ""
      ? Number(event.kmDistancia.replace(",", "."))
      : undefined;

  const cantidad =
    typeof event?.cantidad === "string" && event.cantidad.trim() !== ""
      ? Number.parseFloat(event.cantidad.replace(",", "."))
      : undefined;

  const nextFilters = {
    fechaEntrega,
    nroCuenta: event?.nroCuenta || undefined,
    procedencia: event?.procedencia || undefined,
    kmDistancia,
    cantidad,
    estados: estados?.length ? estados : undefined,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFiltersHandlerProps) => {
  setValue("fechaEntrega", undefined);
  setValue("nroCuenta", undefined);
  setValue("procedencia", "");
  setValue("kmDistancia", "");
  setValue("cantidad", "");
  setValue("estados", []);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters({});

  await fetchList({ "P.Pagina": 1 });
};

export const handleDetailHandler = (navigate: (path: string) => void, id: number) => {
  navigate(AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)));
};
